import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Video } from '@meradio/shared-types';
import { serializeVideo, type SerializableVideo } from './playerSlice';

export type DeckId = 'a' | 'b';

export interface DeckState {
  video: SerializableVideo | null;
  isPlaying: boolean;
  cuePoint: number | null;
}

export interface DecksState {
  djMode: boolean;
  crossfader: number;
  decks: {
    a: DeckState;
    b: DeckState;
  };
}

const emptyDeck = (): DeckState => ({ video: null, isPlaying: false, cuePoint: null });

const initialState: DecksState = {
  djMode: false,
  crossfader: 0.5,
  decks: {
    a: emptyDeck(),
    b: emptyDeck(),
  },
};

const decksSlice = createSlice({
  name: 'decks',
  initialState,
  reducers: {
    enterDjMode: (state, action: PayloadAction<SerializableVideo | null>) => {
      state.djMode = true;
      state.crossfader = 0.5;
      state.decks.a = { video: action.payload, isPlaying: Boolean(action.payload), cuePoint: null };
      state.decks.b = emptyDeck();
    },

    exitDjMode: (state) => {
      state.djMode = false;
      state.crossfader = 0.5;
      state.decks.a = emptyDeck();
      state.decks.b = emptyDeck();
    },

    loadDeck: (state, action: PayloadAction<{ deck: DeckId; video: Video }>) => {
      const { deck, video } = action.payload;
      state.decks[deck] = { video: serializeVideo(video), isPlaying: false, cuePoint: null };
    },

    setDeckPlaying: (state, action: PayloadAction<{ deck: DeckId; isPlaying: boolean }>) => {
      const { deck, isPlaying } = action.payload;
      state.decks[deck].isPlaying = isPlaying;
    },

    setDeckCue: (state, action: PayloadAction<{ deck: DeckId; cuePoint: number | null }>) => {
      const { deck, cuePoint } = action.payload;
      state.decks[deck].cuePoint = cuePoint;
    },

    clearDeck: (state, action: PayloadAction<DeckId>) => {
      state.decks[action.payload] = emptyDeck();
    },

    setCrossfader: (state, action: PayloadAction<number>) => {
      state.crossfader = Math.max(0, Math.min(1, action.payload));
    },
  },
});

export const {
  enterDjMode,
  exitDjMode,
  loadDeck,
  setDeckPlaying,
  setDeckCue,
  clearDeck,
  setCrossfader,
} = decksSlice.actions;

export const selectDjMode = (state: { decks: DecksState }) => state.decks.djMode;
export const selectCrossfader = (state: { decks: DecksState }) => state.decks.crossfader;
export const selectDecks = (state: { decks: DecksState }) => state.decks.decks;
export const selectDeckA = (state: { decks: DecksState }) => state.decks.decks.a;
export const selectDeckB = (state: { decks: DecksState }) => state.decks.decks.b;

export const selectAudibleDeck = (state: { decks: DecksState }): DeckId => {
  const x = state.decks.crossfader;
  if (x < 0.5) return 'a';
  if (x > 0.5) return 'b';
  return 'a';
};

export default decksSlice.reducer;
